
























































































import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'
import LoaderWrapper from '@/components/layout/LoaderWrapper.vue'
import SearchInput from '@/components/inputs/SearchInput.vue'
import TableHeadFilters from '@/components/tables/TableHeadFilters.vue'
import TableBody from '@/components/tables/TableBody.vue'
import ResidentLabel from '@/components/labels/ResidentLabel.vue'
import FacilityLabel from '@/components/labels/FacilityLabel.vue'
import ScrollView from '@/components/ScrollView.vue'
import Pagination from '@/components/tables/Pagination.vue'
import NoRows from '@/components/tables/NoRows.vue'
import { ETableColumnType, ITableColumn, ITableConfig } from '@/interfaces'
// @ts-ignore
import { v4 as uuidv4 } from 'uuid'
import { Debounce } from 'vue-debounce-decorator'
/**
 * @typedef {Object} Config
 *
 * @param {Boolean} useQuery - enable/disable url query params parsing
 * @param {Boolean} usePagination - enable/disable pagination
 * @param {Boolean} useSubrow  - enable/disable subrow slot inside TableBody
 *
 * @param {String} minHeight - sets scroll view container min height style
 * @example '100px'
 *
 * @param {String} maxHeight - sets scroll view container max height style
 * @example '100px'
 *
 * @param {Function} rowClick - emits when row is clicked. passes row, row index
 * @example rowClick: this.clickRow
 *
 * @param {Function} fetchFunction - emits on mounted and on filter change. passes filters as func arg
 * @example fetchFunction: this.fetchFunction
 *
 * @param {Object} Columns
 */

/**
 * @typedef {Object} Column
 *
 * @param {String} minWidth - sets min col width
 * @example '100px'
 *
 * @param {Number} width - sets col width in percents
 * @example 10
 *
 * @param {String} class - sets class/classes for column wrapper
 * @example 'text-right font-weight-semi-bold'
 *
 * @param {String} key - is used to access col slot
 * @example 'v-slot:name="[item, index]"'
 *
 * @param {String} searchKey - is used for filter property
 *
 * @param {Boolean} hasSearch - enable/disable search field for column
 *
 * @param {String} label - is used for label text and input placeholder
 *
 * @param {String} rowClass - is used for set class to body row
 * @param {String} type - is used to select certain header input component
 * @value default, date, select
 *
 * @param {Array} options - is used with type select to set list of select options
 * @example [{
        key: EUserStatus.ACTIVE,
        value: EUserStatus.ACTIVE,
        content: 'Active'
      }]
 */

let debounceT = 800

@Component({
  components: {
    NoRows,
    Pagination,
    ScrollView,
    FacilityLabel,
    ResidentLabel,
    TableBody,
    TableHeadFilters,
    SearchInput,
    LoaderWrapper
  },
  props: {
    /** @type {Object} config */
    config: {
      type: Object as PropType<ITableConfig>,
      required: true
    },
    /** WARNING!!! Do not pass empty object -> {}. Causes infinite loop. Pass null instead */
    filters: {
      type: Object,
      default: () => {
        return {
          limit: 20,
          skip: 0
        }
      }
    },
    rows: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    syncScroll: {
      type: Boolean,
      default: false
    },
    freezeClicked: {
      type: Boolean,
      default: false
    }
  }
})
export default class Table extends Vue {
  isLoading: boolean = false
  scrollSwitcher: boolean = false
  filterObj: any = {}
  debounce: number = 800
  id: string = 'a' + uuidv4()
  columnsMinWidthSum: string = 'auto'

  setColumnsMinWidthSum () {
    const colsWidth: number = this.$props.config.columns.reduce((acc: number, item: any) => {
      return item.minWidth
        ? parseInt(item.minWidth.replace('px', ''), 10) + acc
        : acc
    }, 0) + this.$props.config.columns.length * 8

    const wrapper: any = this.$refs.tableWrapper
    if (!wrapper) {
      return 'auto'
    }
    this.columnsMinWidthSum = colsWidth > wrapper.$el.offsetWidth ? `${colsWidth}px` : `${wrapper.$el.offsetWidth}px`
  }

  beforeMount () {
    if (this.$props.config.debounceTime !== undefined) {
      debounceT = this.$props.config.debounceTime
    }
    this.filterObj = { ...this.$props.filters }
    if (this.$props.config.columns.find((col: ITableColumn) => col.hasSort)) {
      if (!this.$props.filters.sortBy) {
        this.$set(this.filterObj, 'sortBy', '')
      }
      if (!this.$props.filters.sortOrder) {
        this.$set(this.filterObj, 'sortOrder', '')
      }
    }

    if (this.$props.config.useQuery) {
      this.$props.config.columns.forEach((column: ITableColumn) => {
        if (column.hasSearch && !this.filterObj[column.searchKey as string]) {
          this.$set(this.filterObj, column.searchKey as string, '')
        }
      })

      const query = JSON.parse(JSON.stringify(this.routeQuery))
      Object.keys(this.filterObj).forEach((key: string) => {
        if (!query[key]) {
          return
        }
        if (this.$props.config.columns.findIndex((col: ITableColumn) => col.type === ETableColumnType.multiselect && col.searchKey === key) > -1) {
          const arr = Array.isArray(query[key]) ? query[key] : [query[key]]
          this.$set(this.filterObj, key as string, arr)
        } else {
          this.filterObj[key] = query[key]
        }
      })
    }
  }

  get routeQuery () {
    const query: any = { ...this.$route.query }
    if (query.limit && query.skip) {
      query.limit = parseInt(query.limit)
      query.skip = parseInt(query.skip)
    } else {
      return {}
    }
    return query
  }

  scrollYEvent (event: any) {
    this.$emit('scrollY', event.target)
  }

  async mounted () {
    if (this.$props.syncScroll) {
      const srollArea: any = this.$refs.scrollArea
      if (srollArea) {
        srollArea.$el.addEventListener('scroll', this.scrollYEvent)
      }
    }

    this.onResize()
    await this.initSearch()

    this.$watch('$props.filters', (newValue) => {
      // @ts-ignore
      this.$refs['tableHeader'].clearFilters()

      this.filterObj = { ...newValue }
      this.initSearch()

      // @ts-ignore
      this.$refs['tableHeader'].parseQuery()
    }, { deep: true })

    this.onResize()
    window.addEventListener('resize', this.onResize)
  }

  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
  }

  onResize () {
    this.setColumnsMinWidthSum()
  }

  filterChanged (value: object) {
    this.filterObj = {
      ...this.filterObj,
      ...value,
      skip: 0
    }
    this.initSearch()
  }

  debounceFilterChanged (value: object) {
    this.filterObj = {
      ...this.filterObj,
      ...value,
      skip: 0
    }
    this.debounceInitSearch()
  }

  @Debounce(debounceT)
  debounceInitSearch () {
    this.initSearch()
  }

  paginate (value: any) {
    this.filterObj = {
      ...this.filterObj,
      skip: value
    }
    this.initSearch()
  }

  changePageSize (value: any) {
    this.filterObj = {
      ...this.filterObj,
      limit: value || 20,
      skip: 0
    }
    this.initSearch()
  }

  parseFilters () {
    for (let key in this.filterObj) {
      if (
        this.filterObj.hasOwnProperty(key) &&
        !this.filterObj[key] &&
        this.filterObj[key] !== 0 &&
        this.filterObj[key] !== false
      ) {
        delete this.filterObj[key]
        continue
      }

      if (
        this.filterObj.hasOwnProperty(key) &&
        this.$props.config.columns.findIndex((col: ITableColumn) => col.type === ETableColumnType.multiselect && col.searchKey === key) > -1
      ) {
        this.filterObj[key] = Array.isArray(this.filterObj[key])
          ? this.filterObj[key].map((obj: any) => obj.value ? obj.value : obj)
          : [this.filterObj[key]]
      }
    }

    if (this.$props.config.useQuery && JSON.stringify(this.routeQuery) !== JSON.stringify(this.filterObj)) {
      this.$router.replace({
        query: {
          ...this.filterObj as any
        }
      })
    }
  }

  async initSearch () {
    if (!this.$props.config.fetchFunction) {
      return
    }
    try {
      this.isLoading = true
      this.parseFilters()
      await this.$props.config.fetchFunction(this.filterObj)
    } finally {
      this.$emit('afterDataReload', this.filterObj)
      this.$nextTick(() => {
        this.onResize()
        this.isLoading = false
      })
    }
  }
}
